import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import Modal from '../../BaseComponents/Modal';
import {
  createDoctor,
  getDoctorsList,
  updateDoctor
} from '../../../api/doctors';
import './DoctorsModal.scss';
import TextInput from '../../BaseComponents/TextInput';
import TextArea from '../../BaseComponents/TextArea';
import Button from '../../BaseComponents/Button';
import Select from '../../BaseComponents/Select';
import { DoctorSpecializations, specNames } from '../../../utils/constants';
import { DoctorSpecializationEnum as ApiDoctorSpecializationEnum } from '../../../types/apiGeneratedTypes';
// import UploadPhoto from '../../UploadPhoto';
import { useDispatch, useSelector } from 'react-redux';
import {
  DoctorsPageState,
  setDoctorsCount,
  setDoctorsList
} from '../../../store/doctorsSlice';
import UploadPhoto from '../../UploadPhoto';
import { deleteFile } from '../../../api/fileStorage';
import PhoneInputComp from '../../BaseComponents/PhoneInput';
import { toast } from 'react-toastify';
import { SelectOption } from '../../BaseComponents/Select';
import { getTeamsList } from '../../../api/teams';

type TypeName = {
  isOpen: boolean;
  onClose: () => void;
};

interface FormValues {
  first_name: string;
  last_name: string;
  specialization: { key: ApiDoctorSpecializationEnum; value: string };
  bio: string;
  email: string;
  number: string;
  photo: string;
  calendly_url: string;
  blood_consult_calendly_url: string;
  fbs_consult_calendly_url: string;
  pre_scan_consult_calendly_url: string;
  photo_url?: any;
}

const DoctorsModal: React.FC<TypeName> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { doctorDataToEdit, doctorsList, doctorsCount, limit } = useSelector(
    (state: { doctors: DoctorsPageState }) => state.doctors
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFormError, setIsFormError] = useState<boolean>(false);
  const [wasModalChanged, setWasModalChanged] = useState<boolean>(false);
  const [selectedTeam, setSelectedTeam] = useState<SelectOption | null>(null);
  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchTeamOptions = async () => {
      try {
        const response = await getTeamsList(0, 100);
        const options: SelectOption[] = response.teams.map((team) => ({
          key: team.id,
          value: team.name,
        }));
        setTeamOptions(options);

        const doctorTeamId = doctorDataToEdit?.team_id || '';
        const doctorTeamName = doctorDataToEdit?.team_name || '';

        if (doctorTeamId) {
          const matchedById = options.find((opt) => opt.key === doctorTeamId);
          setSelectedTeam(
            matchedById || { key: doctorTeamId, value: doctorTeamName || doctorTeamId }
          );
        } else if (doctorTeamName) {
          const matchedByName = options.find((opt) => opt.value === doctorTeamName);
          setSelectedTeam(
            matchedByName || { key: doctorTeamName, value: doctorTeamName }
          );
        } else {
          setSelectedTeam(null);
        }
      } catch (error: any) {
        setTeamOptions([]);
        setSelectedTeam(null);
        toast.error(error?.message || 'Failed to load teams');
      }
    };

    fetchTeamOptions();
  }, [isOpen, doctorDataToEdit]);

  const initialValues: FormValues = {
    first_name: doctorDataToEdit?.first_name || '',
    last_name: doctorDataToEdit?.last_name || '',
    specialization: {
      key:
        doctorDataToEdit?.specialization || ApiDoctorSpecializationEnum.Value1,
      value:
        specNames[
          doctorDataToEdit?.specialization || ApiDoctorSpecializationEnum.Value1
        ]
    },
    bio: doctorDataToEdit?.bio || '',
    email: doctorDataToEdit?.email || '',
    number: doctorDataToEdit?.phone || '',
    photo: doctorDataToEdit?.photo || '',
    photo_url: doctorDataToEdit?.photo_url || '',
    calendly_url: doctorDataToEdit?.calendly_url || '',
    blood_consult_calendly_url: doctorDataToEdit?.blood_consult_calendly_url || '',
    fbs_consult_calendly_url: doctorDataToEdit?.fbs_consult_calendly_url || '',
    pre_scan_consult_calendly_url:
      doctorDataToEdit?.pre_scan_consult_calendly_url || '',
  };

  const onUploadPhoto = (base64Photo: string) => {
    setWasModalChanged(true);
  };

  const onModalClose = (photo: string) => {
    if (wasModalChanged) {
      deleteFile({ file_keys: [photo] });
    }
    setSelectedTeam(null);
    onClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, { resetForm }) => {
        if (isFormError) setIsFormError(false);
        setIsLoading(true);
        try {
          const phone =
            values.number[0] !== '+' ? '+' + values.number : values.number;
          const doctorData = {
            email: values.email,
            phone: phone.length <= 4 ? undefined : phone,
            specialization: values.specialization.key || null,
            bio: values.bio,
            first_name: values.first_name,
            last_name: values.last_name,
            photo: values.photo ?? doctorDataToEdit?.photo!,
            team_id: selectedTeam?.key || null,
            calendly_url: values.calendly_url || null,
            blood_consult_calendly_url: values.blood_consult_calendly_url || null,
            fbs_consult_calendly_url: values.fbs_consult_calendly_url || null,
            pre_scan_consult_calendly_url:
              values.pre_scan_consult_calendly_url || null,
          };

          doctorDataToEdit
            ? await updateDoctor({
                ...doctorData,
                user_id: doctorDataToEdit.id
              })
            : await createDoctor(doctorData);

          if (doctorDataToEdit) {
            const updatedDoctorsList = doctorsList?.map((doctor) => {
              if (doctor.id === doctorDataToEdit.id) {
                return {
                  ...doctor,
                  ...doctorData,
                  team_id: selectedTeam?.key || null,
                  team_name: selectedTeam?.value || '',
                  photo_url: values.photo_url
                };
              }
              return doctor;
            });
            dispatch(setDoctorsList(updatedDoctorsList!));
          } else {
            const response = await getDoctorsList(0, limit);
            dispatch(setDoctorsList(response.doctors));
            dispatch(setDoctorsCount(doctorsCount ? doctorsCount + 1 : 0));
          }

          onClose();
          resetForm();
        } catch (error: any) {
          console.error('Error creating doctor:', error);
          setIsFormError(true);
          let errorMessage = 'Something went wrong. Please try again.';

          if (error?.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          } else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error?.message) {
            errorMessage = error.message;
          }

          toast.error(errorMessage);
        } finally {
          setIsLoading(false);
        }
      }}
      // validate={validate}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize={true}
    >
      {({ handleSubmit, values, submitCount, errors, setFieldValue }) => (
        <Modal
          isOpen={isOpen}
          onClose={() => onModalClose(values.photo!)}
          title={'Add doctor'}
          width={'520px'}
        >
          <form
            onSubmit={handleSubmit}
            noValidate
            className='doctors-modal-container'
          >
            <div className='main-info-container'>
              <div className='section-title'>Main info</div>
              <div className='upload-photo-wrapper'>
                <UploadPhoto onPhotoChange={onUploadPhoto} />
              </div>
              <div className='inputs-wrapper'>
                <TextInput
                  name='first_name'
                  placeholder='Enter first name *'
                  type='text'
                  isError={submitCount > 0 && !!errors.first_name}
                  errorMessage={submitCount > 0 ? errors.first_name : undefined}
                  onChange={(e) => setFieldValue('first_name', e.target.value)}
                  value={values.first_name}
                />
                <TextInput
                  name='last_name'
                  placeholder='Enter last name *'
                  type='text'
                  isError={submitCount > 0 && !!errors.last_name}
                  errorMessage={submitCount > 0 ? errors.last_name : undefined}
                  onChange={(e) => setFieldValue('last_name', e.target.value)}
                  value={values.last_name}
                />
                <Select
                  name='specialization'
                  options={DoctorSpecializations}
                  selectedOption={values.specialization}
                  onChange={(option) => {
                    setFieldValue('specialization', option);
                  }}
                  isError={submitCount > 0 && !!errors.specialization}
                  errorMessage={
                    errors.specialization && 'Specialization is required'
                  }
                  placeholder='Choose specialization *'
                />
                <Select
                  name='team'
                  options={teamOptions}
                  selectedOption={selectedTeam || { key: '', value: '' }}
                  onChange={(option) => setSelectedTeam(option)}
                  isError={false}
                  placeholder='Select team'
                />
                <TextInput
                  name='calendly_url'
                  placeholder='Calendly URL'
                  type='text'
                  onChange={(e) =>
                    setFieldValue('calendly_url', e.target.value)
                  }
                  value={values.calendly_url}
                />
                <TextInput
                  name='blood_consult_calendly_url'
                  placeholder='Blood consult Calendly URL'
                  type='text'
                  onChange={(e) =>
                    setFieldValue('blood_consult_calendly_url', e.target.value)
                  }
                  value={values.blood_consult_calendly_url}
                />
                <TextInput
                  name='fbs_consult_calendly_url'
                  placeholder='FBS consult Calendly URL'
                  type='text'
                  onChange={(e) =>
                    setFieldValue('fbs_consult_calendly_url', e.target.value)
                  }
                  value={values.fbs_consult_calendly_url}
                />
                <TextInput
                  name='pre_scan_consult_calendly_url'
                  placeholder='Pre-scan consult Calendly URL'
                  type='text'
                  onChange={(e) =>
                    setFieldValue(
                      'pre_scan_consult_calendly_url',
                      e.target.value
                    )
                  }
                  value={values.pre_scan_consult_calendly_url}
                />
                <TextArea
                  name='bio'
                  placeholder='Enter bio *'
                  isError={submitCount > 0 && !!errors.bio}
                  errorMessage={submitCount > 0 ? errors.bio : undefined}
                  onChange={(e) => setFieldValue('bio', e.target.value)}
                  value={values.bio}
                />
              </div>
            </div>
            <div className='contact-info-container'>
              <div className='section-title'>Contact info</div>
              <div className='inputs-wrapper'>
                <TextInput
                  name='email'
                  placeholder='Enter email *'
                  type='email'
                  isError={submitCount > 0 && !!errors.email}
                  errorMessage={submitCount > 0 ? errors.email : undefined}
                  onChange={(e) => setFieldValue('email', e.target.value)}
                  value={values.email}
                />
                <PhoneInputComp
                  placeholder='Enter number'
                  selectedPhone={values.number}
                  onPhoneChange={(phone) => setFieldValue('number', phone)}
                />
              </div>
              <div className='btn-wrapper'>
                <Button
                  text={doctorDataToEdit ? 'Edit doctor' : 'Add doctor'}
                  type='submit'
                  isLoading={isLoading}
                  isDisabled={
                    !!(
                      !values.last_name ||
                      !values.first_name ||
                      !values.email ||
                      !values.specialization.value ||
                      !values.bio ||
                      !values.photo
                    )
                  }
                />
              </div>
            </div>
          </form>
        </Modal>
      )}
    </Formik>
  );
};

export default DoctorsModal;
